import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, UserPlus, Pencil, Ban, CirclePlay, Trash2, FileText, CheckCircle2, XCircle, Crown, Cpu, MemoryStick, Activity, HardDrive, LifeBuoy, Send, ChevronDown, Eye, RefreshCw, LayoutDashboard, Wrench, KeyRound, UserCheck, Eraser, CalendarClock, ShieldBan, Network, Gauge } from "lucide-react";
import { api, formatApiError, fmtEUR, fmtPay, fmtGB, fmtBytes } from "../lib/api";
import { GlassCard, PageHeader, Modal, Spinner } from "../components/common";
import { fmtStorage } from "../lib/planStyles";
import { useAuth } from "../context/AuthContext";

const ROLE_LABEL = { super_admin: "Administrador", reseller: "Revendedor" };
const INV_STATUS = {
  pending: { label: "Pendente", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
  paid: { label: "Paga", cls: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
  cancelled: { label: "Cancelada", cls: "text-slate-500 border-slate-700 bg-slate-800/40" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return d.toLocaleDateString("pt-PT");
};

const daysLeft = (iso) => (iso ? Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000) : null);

const daysLeftText = (iso) => {
  const d = daysLeft(iso);
  if (d === null) return "sem validade";
  if (d < 0) return `vencido há ${-d} ${-d === 1 ? "dia" : "dias"}`;
  if (d === 0) return "vence hoje";
  return `${d} ${d === 1 ? "dia restante" : "dias restantes"}`;
};

function StorageCard() {
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({ access_key: "", secret_key: "", endpoint_url: "https://s3.us-central-1.wasabisys.com", region: "us-central-1", bucket: "" });
  const [src, setSrc] = useState({ src_access_key: "", src_secret_key: "", src_endpoint_url: "https://s3.us-central-1.wasabisys.com", src_region: "us-central-1", src_bucket: "wexon" });
  const [prefix, setPrefix] = useState("");
  const [busy, setBusy] = useState("");
  const load = () => api.get("/s3/config").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setS = (k) => (e) => setSrc({ ...src, [k]: e.target.value });
  const valid = form.access_key.trim().length >= 8 && form.secret_key.trim().length >= 8 && form.endpoint_url.trim() && form.bucket.trim();
  const srcFilled = src.src_access_key.trim().length >= 8 && src.src_secret_key.trim().length >= 8 && src.src_bucket.trim();
  const canMigrate = valid && (srcFilled || cfg?.previous || (cfg?.configured && cfg.bucket !== form.bucket.trim()));
  const save = async () => {
    setBusy("save");
    try {
      await api.put("/s3/config", form);
      toast.success("Nova conta de storage ativada e validada");
      setForm({ ...form, access_key: "", secret_key: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const migrate = async (useCurrent) => {
    setBusy(useCurrent ? "migcur" : "mig");
    try {
      await api.post("/s3/migrate/start", useCurrent ? { ...src, prefix } : { ...form, ...(srcFilled ? src : {}), prefix });
      toast.success("Migração iniciada — acompanhe o progresso na página Fila (tipo: Migração de storage)");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  return (
    <GlassCard className="p-6 anim-fade-up" data-testid="storage-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
          <HardDrive size={18} className="text-cyan-400" />
        </div>
        <div>
          <h3 className="font-display text-white font-semibold text-sm">Armazenamento em nuvem (troca de conta)</h3>
          <p className="text-[11px] text-slate-500">Troque a conta do provedor de storage e/ou migre todo o conteúdo preservando as pastas</p>
        </div>
      </div>

      {cfg?.configured && (
        <div className="rounded-xl border border-slate-800/60 bg-slate-950/40 p-4 mb-4" data-testid="storage-current">
          <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-2">Conta ativa agora</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono2">
            <span className="text-slate-300">bucket: <b className="text-cyan-300">{cfg.bucket}</b></span>
            <span className="text-slate-300">região: <b className="text-cyan-300">{cfg.region}</b></span>
            <span className="text-slate-300">endpoint: <b className="text-cyan-300">{cfg.endpoint_url}</b></span>
            <span className="text-slate-300">chave: <b className="text-cyan-300">{cfg.access_key}</b></span>
          </div>
          {cfg.validated_at && <p className="text-[10px] text-slate-600 mt-1.5">validada em {new Date(cfg.validated_at).toLocaleString("pt-PT")}</p>}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Access Key (nova conta)</label>
          <input data-testid="storage-access-key" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={form.access_key} onChange={set("access_key")} placeholder="Ex: ABC123..." autoComplete="off" />
        </div>
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Secret Key (nova conta)</label>
          <input data-testid="storage-secret-key" type="password" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={form.secret_key} onChange={set("secret_key")} placeholder="••••••••" autoComplete="new-password" />
        </div>
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Endpoint</label>
          <input data-testid="storage-endpoint" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={form.endpoint_url} onChange={set("endpoint_url")} placeholder="https://s3.us-central-1.wasabisys.com" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Região</label>
            <input data-testid="storage-region" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={form.region} onChange={set("region")} placeholder="us-central-1" />
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Bucket</label>
            <input data-testid="storage-bucket" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={form.bucket} onChange={set("bucket")} placeholder="meu-bucket" />
          </div>
        </div>
      </div>

      {cfg?.previous && (
        <div className="rounded-xl border border-violet-400/25 bg-violet-400/5 p-4 mb-4" data-testid="storage-previous">
          <p className="text-[10px] font-mono2 uppercase tracking-widest text-violet-300 mb-1.5">Conta anterior (arquivada — será a origem da migração)</p>
          <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs font-mono2">
            <span className="text-slate-300">bucket: <b className="text-violet-300">{cfg.previous.bucket}</b></span>
            <span className="text-slate-300">endpoint: <b className="text-violet-300">{cfg.previous.endpoint_url}</b></span>
            <span className="text-slate-300">chave: <b className="text-violet-300">{cfg.previous.access_key}</b></span>
          </div>
        </div>
      )}

      <div className="rounded-xl border border-slate-800/60 bg-slate-950/40 p-4 mb-4" data-testid="storage-source">
        <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-1">Conta antiga — origem da migração</p>
        <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
          Se já ativou a conta nova, preencha <b>somente esta seção</b> com as chaves da conta antiga e use o botão "Migrar conta antiga → conta ativa". As chaves antigas ficam no painel da Wasabi → Access Keys.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input data-testid="storage-src-access-key" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={src.src_access_key} onChange={setS("src_access_key")} placeholder="Access Key da conta antiga" autoComplete="off" />
          <input data-testid="storage-src-secret-key" type="password" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={src.src_secret_key} onChange={setS("src_secret_key")} placeholder="Secret Key da conta antiga" autoComplete="new-password" />
          <input data-testid="storage-src-endpoint" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={src.src_endpoint_url} onChange={setS("src_endpoint_url")} placeholder="Endpoint da conta antiga" />
          <div className="grid grid-cols-2 gap-3">
            <input data-testid="storage-src-region" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={src.src_region} onChange={setS("src_region")} placeholder="Região antiga" />
            <input data-testid="storage-src-bucket" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={src.src_bucket} onChange={setS("src_bucket")} placeholder="Bucket antigo (ex: wexon)" />
          </div>
        </div>
        <div className="mt-3">
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Migrar só uma pasta (opcional)</label>
          <input data-testid="storage-migrate-prefix" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" value={prefix} onChange={(e) => setPrefix(e.target.value)} placeholder="Ex: cinetuga_bot — vazio migra o bucket inteiro" />
          <p className="text-[10px] text-slate-600 mt-1.5">Cada cliente tem uma pasta própria no storage (ex.: o Tuga Team usa <b className="text-slate-400">cinetuga_bot</b>). Para migrar só o conteúdo dele, escreva <b className="text-slate-400">cinetuga_bot</b> aqui.</p>
        </div>
      </div>

      <div className="rounded-xl border border-amber-400/25 bg-amber-400/5 p-3.5 mb-4" data-testid="storage-warning">
        <p className="text-[11px] text-amber-200/90 leading-relaxed">
          <b>Ordem certa da troca:</b> 1) crie o bucket na conta nova do provedor · 2) clique em <b>Migrar conteúdo</b> (copia tudo preservando as pastas — acompanhe na Fila) · 3) quando a migração concluir, clique em <b>Ativar esta conta</b>. Se ativar antes de migrar, os vídeos antigos ficam fora do ar até a cópia terminar.
        </p>
      </div>

      <div className="flex flex-wrap gap-3">
        {cfg?.configured && (
          <button data-testid="storage-migrate-current-btn" onClick={() => migrate(true)} disabled={!srcFilled || cfg.bucket === src.src_bucket.trim() || busy === "migcur"} className="btn-neon px-5 py-2.5 rounded-xl text-xs inline-flex items-center gap-2 disabled:opacity-40">
            {busy === "migcur" ? <Spinner /> : <RefreshCw size={13} />} Migrar conta antiga → conta ativa ({cfg.bucket})
          </button>
        )}
        <button data-testid="storage-migrate-btn" onClick={() => migrate(false)} disabled={!canMigrate || busy === "mig"} className="px-5 py-2.5 rounded-xl text-xs font-semibold border border-violet-400/40 text-violet-300 hover:bg-violet-500/10 transition-colors inline-flex items-center gap-2 disabled:opacity-40">
          {busy === "mig" ? <Spinner /> : <RefreshCw size={13} />} Migrar conteúdo para a conta do formulário
        </button>
        <button data-testid="storage-save-btn" onClick={save} disabled={!valid || busy === "save"} className="btn-neon px-5 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
          {busy === "save" ? <Spinner /> : <CheckCircle2 size={13} />} Ativar esta conta
        </button>
      </div>
    </GlassCard>
  );
}

function ReconcileCard() {
  const [rec, setRec] = useState(null);
  const [recBusy, setRecBusy] = useState(false);
  const pollRec = () => {
    const t = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/acervo/reconcile");
        setRec(data);
        if (data.status !== "running") clearInterval(t);
      } catch { clearInterval(t); }
    }, 3000);
  };
  const runReconcile = async () => {
    setRecBusy(true);
    try {
      await api.post("/admin/acervo/reconcile");
      setRec({ status: "running", scanned: 0 });
      pollRec();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRecBusy(false);
    }
  };
  return (
    <GlassCard className="p-6 anim-fade-up" data-testid="admin-reconcile-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-violet-400/10 border border-violet-400/25 flex items-center justify-center shrink-0">
          <RefreshCw size={18} className="text-violet-400" />
        </div>
        <div>
          <h3 className="font-display text-white font-semibold text-sm">Reconciliação de armazenamento</h3>
          <p className="text-[11px] text-slate-500">Remove registros cujo arquivo não existe mais na nuvem e recalcula a cota de GB de cada conta</p>
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3" data-testid="admin-reconcile-block">
        <button data-testid="reconcile-run-btn" onClick={runReconcile} disabled={recBusy || rec?.status === "running"} className="px-4 py-2.5 rounded-xl text-xs font-semibold border border-violet-400/40 text-violet-300 hover:bg-violet-500/10 transition-colors flex items-center gap-2">
          {(recBusy || rec?.status === "running") ? <Spinner /> : <RefreshCw size={13} />} Reconciliar acervo com a nuvem
        </button>
        {rec?.status === "running" && (
          <span className="text-[11px] font-mono2 text-cyan-300" data-testid="reconcile-progress">varrendo… {rec.scanned || 0} registros verificados</span>
        )}
        {rec?.status === "done" && (
          <span className="text-[11px] font-mono2 text-emerald-300" data-testid="reconcile-result">
            ✅ {rec.scanned} verificados · {rec.stale} fantasmas removidos ({((rec.stale_bytes || 0) / 1e9).toFixed(1)} GB) · {rec.titles_removed} títulos órfãos limpos · cotas recalculadas
          </span>
        )}
        {rec?.status === "failed" && <span className="text-[11px] font-mono2 text-rose-300" data-testid="reconcile-error">falhou: {rec.error}</span>}
      </div>
    </GlassCard>
  );
}

function AdminCentral() {
  const [ov, setOv] = useState(null);
  useEffect(() => {
    const load = () => api.get("/admin/overview").then((r) => setOv(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);
  if (!ov) return null;
  const q = ov.queue || {};
  return (
    <GlassCard className="p-6 anim-fade-up" data-testid="admin-central-card">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-10 h-10 rounded-xl bg-violet-400/10 border border-violet-400/25 flex items-center justify-center shrink-0">
          <Activity size={18} className="text-violet-400" />
        </div>
        <div>
          <h3 className="font-display text-white font-semibold text-sm">Central administrativa</h3>
          <p className="text-[11px] text-slate-500">Visão geral de clientes, fila de importação e atividade recente — clique no 👁 de um cliente para abrir o ambiente dele</p>
        </div>
      </div>
      <div className="grid grid-cols-4 lg:grid-cols-8 gap-3 mb-5" data-testid="admin-central-stats">
        {[
          ["Clientes", ov.clients_total, "text-white"], ["Ativos", ov.clients_active, "text-emerald-300"], ["Suspensos", ov.clients_suspended, "text-rose-300"],
          ["Na fila", q.queued || 0, "text-amber-300"], ["Importando", q.running || 0, "text-cyan-300"], ["Com erro", q.failed || 0, "text-rose-300"],
          ["Concluídos", q.done || 0, "text-emerald-300"], ["Storage", fmtStorage(ov.storage_total_gb || 0), "text-white"],
        ].map(([label, value, cls]) => (
          <div key={label} className="rounded-xl bg-slate-950/50 border border-slate-800/60 p-3">
            <p className={`font-display text-lg font-bold ${cls}`}>{value}</p>
            <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-500">{label}</p>
          </div>
        ))}
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        <div data-testid="admin-central-accesses">
          <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-2">Últimos acessos (quem assiste)</p>
          <div className="space-y-1.5">
            {(ov.last_accesses || []).length === 0 && <p className="text-[11px] text-slate-600">Nenhum acesso registrado ainda.</p>}
            {(ov.last_accesses || []).map((a) => (
              <p key={a.id} className="text-[11px] font-mono2 text-slate-400 truncate">
                <span className="text-slate-200">{a.name || a.username}</span> · {a.last_ip || "?"} · {a.last_device || "?"} · {fmtDT(a.last_access_at)}
              </p>
            ))}
          </div>
        </div>
        <div data-testid="admin-central-payments">
          <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-2">Últimos pagamentos</p>
          <div className="space-y-1.5">
            {(ov.last_payments || []).length === 0 && <p className="text-[11px] text-slate-600">Nenhum pagamento ainda.</p>}
            {(ov.last_payments || []).map((p) => (
              <p key={p.id || p.external_reference} className="text-[11px] font-mono2 text-slate-400 truncate">
                <span className="text-slate-200">{p.title || p.plan_name || "Pagamento"}</span> · {fmtPay(p)} · {p.status || "?"} · {fmtDT(p.created_at)}
              </p>
            ))}
          </div>
        </div>
        <div data-testid="admin-central-jobs">
          <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mb-2">Últimas importações</p>
          <div className="space-y-1.5">
            {(ov.last_jobs || []).length === 0 && <p className="text-[11px] text-slate-600">Nenhuma importação ainda.</p>}
            {(ov.last_jobs || []).map((j) => (
              <p key={j.id} className="text-[11px] font-mono2 text-slate-400 truncate">
                <span className="text-slate-200">#{j.seq || "?"} {j.kind_filter}</span> · {j.status} · {j.processed || 0}/{j.total || 0} · {fmtDT(j.created_at)}
              </p>
            ))}
          </div>
        </div>
      </div>
    </GlassCard>
  );
}

function YtCookiesCard() {
  const [configured, setConfigured] = useState(null);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/s3/yt-cookies").then((r) => setConfigured(r.data.configured)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async () => {
    setBusy(true);
    try {
      await api.put("/s3/yt-cookies", { content });
      toast.success("Cookies salvos — o importador já usa no próximo download");
      setContent("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await api.delete("/s3/yt-cookies");
      toast.success("Cookies removidos");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <GlassCard className="p-6 anim-fade-up" data-testid="yt-cookies-card">
      <button type="button" data-testid="yt-cookies-toggle" onClick={() => setOpen(!open)} className="flex items-center gap-3 w-full text-left">
        <div className="w-10 h-10 rounded-xl bg-rose-400/10 border border-rose-400/25 flex items-center justify-center shrink-0">
          <ShieldCheck size={18} className="text-rose-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-display text-white font-semibold text-sm">Cookies do importador (YouTube/Google)</h3>
          <p className="text-[11px] text-slate-500">Status: {configured === null ? "…" : configured ? <span className="text-emerald-300" data-testid="yt-cookies-status-on">✅ cookies ativos</span> : <span className="text-slate-500" data-testid="yt-cookies-status-off">nenhum cookie salvo</span>}</p>
        </div>
        <ChevronDown size={16} className={`text-slate-500 shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-4" data-testid="yt-cookies-body">
      <p className="text-[11px] text-slate-500 mb-3 leading-relaxed">
        O YouTube bloqueia downloads vindos de servidores de nuvem ("confirme que não é um robô"). Para libertar: no navegador com sessão iniciada no YouTube,
        exporte os cookies com a extensão <b className="text-slate-300">"Get cookies.txt LOCALLY"</b> e cole o conteúdo do arquivo aqui.
        Links diretos .mp4/.mkv e a maioria dos sites não precisam disso.
      </p>
      <textarea data-testid="yt-cookies-input" rows={4} value={content} onChange={(e) => setContent(e.target.value)}
        placeholder="# Netscape HTTP Cookie File&#10;.youtube.com	TRUE	/	TRUE	…"
        className="input-dark w-full rounded-xl px-4 py-3 text-[11px] font-mono2 mb-3" />
      <div className="flex gap-3">
        <button data-testid="yt-cookies-save" onClick={save} disabled={busy || !content.trim()} className="btn-neon px-5 py-2.5 rounded-xl text-xs">Salvar cookies</button>
        {configured && <button data-testid="yt-cookies-remove" onClick={remove} disabled={busy} className="px-5 py-2.5 rounded-xl text-xs border border-rose-400/40 text-rose-300 hover:bg-rose-500/10 transition-colors">Remover</button>}
          </div>
        </div>
      )}
    </GlassCard>
  );
}

function UserModal({ mode, user, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const initCustom = user?.plan_id?.startsWith("custom_");
  const initGb = Number(user?.plan_storage_gb) || 0;
  const [form, setForm] = useState({
    name: user?.name || "", email: user?.email || "", password: "",
    role: user?.role || "reseller",
    plan_id: initCustom ? "__custom__" : user?.plan_id || "",
    custom_amount: initCustom ? (initGb >= 1024 ? String(Math.round((initGb / 1024) * 100) / 100) : String(initGb)) : "",
    custom_unit: initCustom && initGb < 1024 ? "GB" : "TB",
    plan_days: 30, plan_expires_at: "",
    max_bots: user?.max_bots ?? 1, is_test: user?.is_test || false,
  });
  const initialPlanId = form.plan_id;
  const initialCustomGb = initCustom ? initGb : 0;
  const customGb = () => {
    const amt = Number(String(form.custom_amount).replace(",", "."));
    if (!amt || amt <= 0) return 0;
    return form.custom_unit === "TB" ? Math.round(amt * 1024 * 100) / 100 : Math.round(amt * 100) / 100;
  };
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/saas/plans").then((r) => setPlans(r.data)).catch(() => {});
  }, []);

  const submit = async () => {
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, email: form.email, role: form.role, max_bots: Math.max(0, Number(form.max_bots) || 0), is_test: !!form.is_test };
        if (form.password) payload.password = form.password;
        const planChanged = form.plan_id !== initialPlanId || (form.plan_id === "__custom__" && customGb() !== initialCustomGb);
        const dateSet = !!form.plan_expires_at;
        const daysChanged = Number(form.plan_days) !== 30;
        if (form.plan_id === "__custom__" && !customGb()) {
          toast.error("Informe o armazenamento personalizado (ex.: 3 TB ou 500 GB)");
          setSaving(false);
          return;
        }
        if (dateSet && !form.plan_id) {
          toast.error("Para definir a data de vencimento, selecione um plano de armazenamento junto");
          setSaving(false);
          return;
        }
        if (planChanged || dateSet || (daysChanged && form.plan_id)) {
          payload.plan_id = form.plan_id;
          if (form.plan_id === "__custom__") payload.plan_custom_gb = customGb();
          if (dateSet) payload.plan_expires_at = form.plan_expires_at;
          else payload.plan_days = Number(form.plan_days) || 30;
        }
        await api.put(`/admin/users/${user.id}`, payload);
        toast.success("Usuário atualizado");
      } else {
        if (!form.password) { toast.error("Informe uma senha"); setSaving(false); return; }
        if (form.plan_id === "__custom__" && !customGb()) { toast.error("Informe o armazenamento personalizado (ex.: 3 TB ou 500 GB)"); setSaving(false); return; }
        const { data: created } = await api.post("/admin/users", { ...form, balance_gb: 0, max_bots: Math.max(0, Number(form.max_bots) || 0) });
        if (form.plan_id) {
          const planPayload = { plan_id: form.plan_id };
          if (form.plan_id === "__custom__") planPayload.plan_custom_gb = customGb();
          if (form.plan_expires_at) planPayload.plan_expires_at = form.plan_expires_at;
          else planPayload.plan_days = Number(form.plan_days) || 30;
          await api.put(`/admin/users/${created.id}`, planPayload);
        }
        toast.success("Usuário criado");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={isEdit ? `Editar ${user.name}` : "Novo usuário"} testid="admin-user-modal">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Nome</label>
          <input data-testid="admin-user-name-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.name} onChange={set("name")} />
        </div>
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Email</label>
          <input data-testid="admin-user-email-input" type="email" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.email} onChange={set("email")} disabled={isEdit && user?.is_owner} />
        </div>
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">{isEdit ? "Nova senha (deixe em branco para manter)" : "Senha"}</label>
          <input data-testid="admin-user-password-input" type="password" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Mínimo 6 caracteres" value={form.password} onChange={set("password")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Papel</label>
            <select data-testid="admin-user-role-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.role} onChange={set("role")} disabled={isEdit && user?.is_owner}>
              <option value="reseller">Revendedor (cliente)</option>
              <option value="super_admin">Administrador (acesso total)</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Limite de bots</label>
            <input data-testid="admin-user-max-bots-input" type="number" min="0" step="1" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.max_bots} onChange={set("max_bots")} />
            <p className="text-[10px] text-slate-500 mt-1.5">Quantos bots a conta pode criar (0 = nenhum)</p>
          </div>
        </div>
        <label className="flex items-center gap-3 rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 cursor-pointer" data-testid="admin-user-test-toggle">
          <input type="checkbox" data-testid="admin-user-test-checkbox" checked={!!form.is_test} onChange={(e) => setForm({ ...form, is_test: e.target.checked })} className="accent-fuchsia-400" />
          <span className="text-xs text-slate-300"><b>Conta de teste</b> — selo visual para diferenciar testers de clientes reais</span>
        </label>
        {isEdit && user?.plan_id && (
          <div data-testid="admin-user-current-expiry" className={`rounded-xl border px-4 py-3 text-xs flex items-center justify-between gap-3 ${(daysLeft(user.plan_expires_at) ?? 0) < 0 ? "border-rose-400/30 bg-rose-400/10 text-rose-200" : "border-slate-700 bg-slate-900/50 text-slate-300"}`}>
            <span>Vencimento atual: <b>{user.plan_name} · {fmtDate(user.plan_expires_at)}</b></span>
            <span className="font-mono2 uppercase tracking-widest text-[10px]">{daysLeftText(user.plan_expires_at)}</span>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Plano de armazenamento</label>
            <select data-testid="admin-user-plan-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.plan_id} onChange={set("plan_id")}>
              <option value="">Sem plano</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>{p.name} — {fmtStorage(p.storage_gb)} ({fmtEUR(p.price_eur ?? p.price_brl)}/mês)</option>
              ))}
              <option value="__custom__">✦ Personalizado — escolher tamanho</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">+ Dias (somam à validade)</label>
            <input data-testid="admin-user-plan-days-input" type="number" min="1" step="1" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.plan_days} onChange={set("plan_days")} />
          </div>
        </div>
        {form.plan_id === "__custom__" && (
          <div data-testid="admin-user-custom-plan" className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Armazenamento personalizado</label>
              <input data-testid="admin-user-custom-amount-input" type="number" min="0" step="any" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Ex: 3" value={form.custom_amount} onChange={set("custom_amount")} />
            </div>
            <div>
              <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Unidade</label>
              <select data-testid="admin-user-custom-unit-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.custom_unit} onChange={set("custom_unit")}>
                <option value="TB">TB (terabytes)</option>
                <option value="GB">GB (gigabytes)</option>
              </select>
            </div>
            {customGb() > 0 && (
              <p data-testid="admin-user-custom-preview" className="col-span-2 text-[11px] text-emerald-300 -mt-1">
                Será aplicado: <b>Sob Medida {fmtStorage(customGb())}</b> de cota para este cliente.
              </p>
            )}
          </div>
        )}
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Ou defina a data exata de vencimento (opcional)</label>
          <input data-testid="admin-user-plan-expiry-input" type="date" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.plan_expires_at} onChange={set("plan_expires_at")} />
          <p className="text-[10px] text-slate-500 mt-1.5">Se preenchida, a data exata substitui o cálculo por dias.</p>
        </div>
        {isEdit && user?.is_owner && (
          <p className="text-[11px] text-amber-300/90">Conta do dono: papel e email não podem ser alterados.</p>
        )}
        <button data-testid="admin-user-save-btn" onClick={submit} disabled={saving || !form.name.trim() || !form.email.trim()} className="btn-neon w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
          {saving ? <Spinner /> : isEdit ? "Salvar alterações" : "Criar usuário"}
        </button>
      </div>
    </Modal>
  );
}

function PlanModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", storage_gb: "", price_eur: "", color: "cyan", badge: "Custom" });
  const [suggested, setSuggested] = useState(null);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    const gb = Number(form.storage_gb);
    if (gb > 10240) {
      api.get(`/saas/plans/suggest?storage_gb=${gb}`).then((r) => setSuggested(r.data.price_eur)).catch(() => setSuggested(null));
    } else {
      setSuggested(null);
    }
  }, [form.storage_gb]);

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), storage_gb: Number(form.storage_gb), color: form.color, badge: form.badge.trim() || "Custom" };
      if (form.price_eur !== "") payload.price_eur = Number(form.price_eur);
      await api.post("/saas/plans", payload);
      toast.success(`Plano "${payload.name}" criado`);
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title="Novo plano de armazenamento" testid="admin-plan-modal">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Nome do plano</label>
          <input data-testid="admin-plan-name-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Ex: Ultra 20TB" value={form.name} onChange={set("name")} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Storage (GB)</label>
            <input data-testid="admin-plan-storage-input" type="number" min="1" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="20480" value={form.storage_gb} onChange={set("storage_gb")} />
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Preço mensal (R$)</label>
            <input data-testid="admin-plan-price-input" type="number" min="0" step="0.01" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder={suggested ? String(suggested) : "149.90"} value={form.price_eur} onChange={set("price_eur")} />
            {suggested && form.price_eur === "" && (
              <p className="text-[11px] text-emerald-300 mt-1.5" data-testid="admin-plan-suggested">Automático (custo + 30%): <b>{fmtEUR(suggested)}</b></p>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Cor</label>
            <select data-testid="admin-plan-color-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.color} onChange={set("color")}>
              <option value="emerald">Verde</option>
              <option value="sky">Azul</option>
              <option value="violet">Roxo</option>
              <option value="orange">Laranja</option>
              <option value="red">Vermelho</option>
              <option value="amber">Dourado</option>
              <option value="cyan">Ciano</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Selo</label>
            <input data-testid="admin-plan-badge-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Sob consulta" value={form.badge} onChange={set("badge")} />
          </div>
        </div>
        <button data-testid="admin-plan-save-btn" onClick={submit} disabled={saving || !form.name.trim() || !Number(form.storage_gb)} className="btn-neon w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
          {saving ? <Spinner /> : "Criar plano"}
        </button>
      </div>
    </Modal>
  );
}

function InvoiceModal({ users, edit, presetUserId, onClose, onSaved }) {
  const [form, setForm] = useState({
    user_id: edit?.user_id || presetUserId || "",
    title: edit?.title || "",
    amount_eur: edit?.amount_eur ?? "",
    credits_gb: edit?.credits_gb ?? "",
    due_date: edit?.due_date || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    setSaving(true);
    try {
      if (edit) {
        await api.put(`/admin/invoices/${edit.id}`, {
          title: form.title, amount_eur: Number(form.amount_eur), credits_gb: Number(form.credits_gb) || 0, due_date: form.due_date,
        });
        toast.success("Fatura atualizada");
      } else {
        await api.post("/admin/invoices", {
          user_id: form.user_id, title: form.title, amount_eur: Number(form.amount_eur),
          credits_gb: Number(form.credits_gb) || 0, due_date: form.due_date,
        });
        toast.success("Fatura criada — o cliente já vê na página de Créditos");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open onClose={onClose} title={edit ? "Editar fatura" : "Nova fatura"} testid="admin-invoice-modal">
      <div className="space-y-4">
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Cliente</label>
          <select data-testid="invoice-user-select" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.user_id} onChange={set("user_id")} disabled={!!edit}>
            <option value="">Selecione o cliente...</option>
            {users.map((u) => <option key={u.id} value={u.id}>{u.name} — {u.email}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Descrição</label>
          <input data-testid="invoice-title-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Ex: Mensalidade painel, Pacote 500 GB..." value={form.title} onChange={set("title")} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Valor (R$)</label>
            <input data-testid="invoice-amount-input" type="number" min="0" step="0.01" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.amount_eur} onChange={set("amount_eur")} />
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Créditos (GB)</label>
            <input data-testid="invoice-credits-input" type="number" min="0" step="1" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.credits_gb} onChange={set("credits_gb")} />
          </div>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Vencimento</label>
            <input data-testid="invoice-due-input" type="date" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={form.due_date} onChange={set("due_date")} />
          </div>
        </div>
        <p className="text-[11px] text-slate-500">Ao ser paga (pelo cliente via Telegram ou marcada por si), os créditos caem no saldo automaticamente.</p>
        <button data-testid="invoice-save-btn" onClick={submit} disabled={saving || !form.user_id || !form.amount_eur || !form.due_date} className="btn-neon w-full py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
          {saving ? <Spinner /> : edit ? "Salvar alterações" : "Criar fatura"}
        </button>
      </div>
    </Modal>
  );
}

const fmtRate = (kbps) => (kbps >= 1024 ? `${(kbps / 1024).toFixed(1)} MB/s` : `${Math.round(kbps)} KB/s`);

function ServerStatus() {
  const [sys, setSys] = useState(null);
  const [rate, setRate] = useState({ up: 0, down: 0 });

  useEffect(() => {
    let live = true;
    let prev = null;
    const load = async () => {
      try {
        const { data } = await api.get("/admin/system");
        if (!live) return;
        if (prev && data.ts > prev.ts) {
          const dt = data.ts - prev.ts;
          setRate({
            up: Math.max(0, (data.net_sent_kb - prev.net_sent_kb) / dt),
            down: Math.max(0, (data.net_recv_kb - prev.net_recv_kb) / dt),
          });
        }
        prev = data;
        setSys(data);
      } catch { /* silencioso */ }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { live = false; clearInterval(t); };
  }, []);

  if (!sys) return null;
  const cpuColor = sys.cpu_percent > 80 ? "rose" : sys.cpu_percent > 60 ? "amber" : "emerald";
  const memColor = sys.mem_percent > 85 ? "rose" : sys.mem_percent > 70 ? "amber" : "emerald";
  const bar = (pct, color) => (
    <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
      <div className={`h-full rounded-full transition-all duration-700 ${color === "rose" ? "bg-rose-400" : color === "amber" ? "bg-amber-400" : "bg-emerald-400"}`} style={{ width: `${Math.min(100, pct)}%` }} />
    </div>
  );

  return (
    <GlassCard className="p-6 anim-fade-up" data-testid="server-status-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${sys.cpu_percent > 80 || sys.mem_percent > 85 ? "bg-rose-400/10 border-rose-400/30" : "bg-emerald-400/10 border-emerald-400/25"}`}>
            <Activity size={18} className={sys.cpu_percent > 80 || sys.mem_percent > 85 ? "text-rose-400" : "text-emerald-400"} />
          </div>
          <div>
            <h3 className="font-display text-white font-semibold text-sm">Status do Servidor</h3>
            <p className="text-[11px] text-slate-500">Atualiza a cada 5s · alerta: CPU &gt; 80% ou RAM &gt; 85%</p>
          </div>
        </div>
        {(sys.cpu_percent > 80 || sys.mem_percent > 85) && (
          <span data-testid="server-status-alert" className="text-[10px] font-mono2 uppercase tracking-widest text-rose-300 border border-rose-400/40 bg-rose-400/10 px-2.5 py-1 rounded-lg animate-pulse">No limite</span>
        )}
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        <div data-testid="server-cpu">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5"><Cpu size={12} /> CPU</span>
            <span className={`text-xs font-bold font-mono2 text-${cpuColor}-300`}>{Math.round(sys.cpu_percent)}%</span>
          </div>
          {bar(sys.cpu_percent, cpuColor)}
          <p className="text-[10px] text-slate-600 mt-1.5">load {sys.load1}</p>
        </div>
        <div data-testid="server-ram">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5"><MemoryStick size={12} /> RAM</span>
            <span className={`text-xs font-bold font-mono2 text-${memColor}-300`}>{sys.mem_used_gb}/{sys.mem_total_gb} GB</span>
          </div>
          {bar(sys.mem_percent, memColor)}
          <p className="text-[10px] text-slate-600 mt-1.5">{Math.round(sys.mem_percent)}% usado</p>
        </div>
        <div data-testid="server-net">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5"><Activity size={12} /> Banda</span>
          </div>
          <p className="text-xs text-slate-300 font-mono2">↓ {fmtRate(rate.down)} · ↑ {fmtRate(rate.up)}</p>
          <p className="text-[10px] text-slate-600 mt-1.5">download · upload agora</p>
        </div>
        <div data-testid="server-jobs">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-400 inline-flex items-center gap-1.5"><FileText size={12} /> Fila</span>
          </div>
          <p className="text-xs text-slate-300 font-mono2">{sys.jobs_running} rodando · {sys.jobs_queued} na fila</p>
          <p className="text-[10px] text-slate-600 mt-1.5">importações</p>
        </div>
      </div>
    </GlassCard>
  );
}

const TICKET_STATUS = {
  open: { label: "Aguardando", cls: "text-amber-300 border-amber-400/30 bg-amber-400/10" },
  answered: { label: "Respondido", cls: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10" },
  closed: { label: "Fechado", cls: "text-slate-500 border-slate-700 bg-slate-800/40" },
};

const fmtDT = (v) => {
  if (!v) return "—";
  const d = typeof v === "number" ? new Date(v * 1000) : new Date(v);
  return isNaN(d) ? "—" : d.toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

function TicketsAdmin() {
  const [tickets, setTickets] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/tickets").then((r) => setTickets(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const send = async (t) => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tickets/${t.id}/reply`, { message: reply.trim() });
      setReply("");
      toast.success("Resposta enviada — cliente avisado no Telegram");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const close = async (t) => {
    setBusy(true);
    try {
      await api.post(`/tickets/${t.id}/close`);
      toast.success("Ticket fechado");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const openCount = (tickets || []).filter((t) => t.status === "open").length;

  return (
    <GlassCard className="overflow-hidden anim-fade-up" data-testid="admin-tickets-card">
      <div className="px-6 pt-6 pb-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center">
            <LifeBuoy size={18} className="text-amber-400" />
          </div>
          <div>
            <h3 className="font-display text-white font-semibold text-sm">Tickets de suporte</h3>
            <p className="text-[11px] text-slate-500">Responder avisa o cliente no Telegram automaticamente</p>
          </div>
        </div>
        {openCount > 0 && (
          <span className="text-[10px] font-mono2 uppercase tracking-widest text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded-full px-2.5 py-1" data-testid="tickets-open-count">
            {openCount} aguardando
          </span>
        )}
      </div>
      <div className="px-6 pb-6">
        {tickets === null ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="tickets-empty-admin">Nenhum ticket — quando um cliente abrir, aparece aqui.</p>
        ) : (
          <div className="space-y-3" data-testid="admin-tickets-list">
            {tickets.map((t) => {
              const s = TICKET_STATUS[t.status] || TICKET_STATUS.open;
              const expanded = openId === t.id;
              return (
                <div key={t.id} className="rounded-xl border border-slate-800/60 bg-slate-950/40" data-testid={`admin-ticket-${t.id}`}>
                  <button
                    data-testid={`admin-ticket-toggle-${t.id}`}
                    onClick={() => { setOpenId(expanded ? null : t.id); setReply(""); }}
                    className="w-full flex flex-wrap items-center gap-3 px-4 py-3.5 text-left"
                  >
                    <ChevronDown size={14} className={`text-slate-500 shrink-0 transition-transform ${expanded ? "rotate-180" : ""}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white font-semibold truncate">{t.subject}</p>
                      <p className="text-[11px] text-slate-500 truncate mt-0.5">{t.user_name} · {t.user_email} · {fmtDT(t.updated_at)}</p>
                    </div>
                    <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-1 rounded-md border ${s.cls}`} data-testid={`admin-ticket-status-${t.id}`}>{s.label}</span>
                  </button>
                  {expanded && (
                    <div className="px-4 pb-4 space-y-3 border-t border-slate-800/60 pt-4">
                      {t.messages.map((m) => (
                        <div key={m.id} className={`max-w-[85%] rounded-xl px-4 py-3 text-xs leading-relaxed ${m.author === "admin" ? "ml-auto bg-amber-400/10 border border-amber-400/25 text-amber-100" : "bg-slate-800/60 border border-slate-700/60 text-slate-200"}`} data-testid={`admin-ticket-msg-${m.id}`}>
                          <p className="whitespace-pre-wrap">{m.text}</p>
                          <p className={`text-[9px] font-mono2 uppercase tracking-widest mt-1.5 ${m.author === "admin" ? "text-amber-400/70" : "text-slate-500"}`}>
                            {m.author === "admin" ? "Suporte (admin)" : t.user_name} · {fmtDT(m.created_at)}
                          </p>
                        </div>
                      ))}
                      {t.status !== "closed" && (
                        <div className="flex gap-2">
                          <input
                            data-testid={`admin-ticket-reply-input-${t.id}`}
                            className="input-dark flex-1 rounded-xl px-4 py-2.5 text-sm"
                            placeholder="Responder ao cliente..."
                            value={reply}
                            onChange={(e) => setReply(e.target.value)}
                          />
                          <button data-testid={`admin-ticket-reply-send-${t.id}`} onClick={() => send(t)} disabled={busy || !reply.trim()} className="btn-neon px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-1.5 shrink-0">
                            {busy ? <Spinner /> : <Send size={13} />} Responder
                          </button>
                          <button data-testid={`admin-ticket-close-${t.id}`} onClick={() => close(t)} disabled={busy} title="Fechar ticket" className="btn-ghost-dark px-3 py-2.5 rounded-xl text-xs text-slate-400 shrink-0">
                            <XCircle size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </GlassCard>
  );
}

function AccessesAdmin() {
  const [rows, setRows] = useState(null);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(null);
  const [renewAcc, setRenewAcc] = useState(null);
  const [renewDays, setRenewDays] = useState(30);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => api.get("/admin/accesses").then((r) => setRows(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const toggle = async (a) => {
    setBusy(`tg-${a.id}`);
    try {
      const { data } = await api.post(`/admin/accesses/${a.id}/toggle`);
      toast.success(data.status === "paused" ? `Acesso ${a.username} pausado — para de tocar na hora` : `Acesso ${a.username} reativado`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const renew = async () => {
    setBusy("renew");
    try {
      const { data } = await api.post(`/admin/accesses/${renewAcc.id}/renew`, { days: Math.max(1, Number(renewDays) || 30) });
      toast.success(`Acesso ${renewAcc.username} renovado até ${fmtDate(data.expires_at)}`);
      setRenewAcc(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const remove = async () => {
    setBusy("del");
    try {
      await api.delete(`/admin/accesses/${confirmDel.id}`);
      toast.success(`Acesso ${confirmDel.username} excluído`);
      setConfirmDel(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const q = search.trim().toLowerCase();
  const filtered = (rows || []).filter((a) => !q || [a.username, a.name, a.owner_name, a.owner_email, a.bot_username].some((v) => (v || "").toLowerCase().includes(q)));

  return (
    <GlassCard className="overflow-hidden anim-fade-up" data-testid="admin-accesses-card">
      <div className="px-6 pt-6 pb-4 flex flex-wrap items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
          <KeyRound size={18} className="text-cyan-400" />
        </div>
        <div className="flex-1 min-w-36">
          <h3 className="font-display text-white font-semibold text-sm">Todos os acessos <span className="text-slate-500 font-normal text-xs">({filtered.length})</span></h3>
          <p className="text-[11px] text-slate-500">Credenciais de playlist de TODAS as revendas — pausar, renovar validade ou excluir</p>
        </div>
        <input data-testid="admin-accesses-search" className="input-dark rounded-lg px-3 py-2 text-xs w-60" placeholder="Buscar utilizador, cliente ou bot" value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>
      {rows === null ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : filtered.length === 0 ? (
        <p className="px-6 pb-8 text-sm text-slate-500" data-testid="admin-accesses-empty">Nenhum acesso encontrado.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[980px]">
            <thead>
              <tr className="text-left text-[10px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                <th className="px-6 py-3">Cliente (revenda)</th>
                <th className="px-4 py-3">Utilizador</th>
                <th className="px-4 py-3">Bot</th>
                <th className="px-4 py-3">Estado</th>
                <th className="px-4 py-3">Contratado</th>
                <th className="px-4 py-3">Validade</th>
                <th className="px-4 py-3">Conexões</th>
                <th className="px-6 py-3 text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => {
                const paused = a.status === "paused";
                const [label, cls] = a.expired
                  ? ["Vencido", "text-rose-300 border-rose-400/30 bg-rose-400/10"]
                  : paused
                    ? ["Pausado", "text-amber-300 border-amber-400/30 bg-amber-400/10"]
                    : ["Ativo", "text-emerald-300 border-emerald-400/30 bg-emerald-400/10"];
                return (
                  <tr key={a.id} className="border-b border-slate-800/40 last:border-0" data-testid={`access-row-${a.id}`}>
                    <td className="px-6 py-3.5">
                      <p className="text-white text-xs font-semibold truncate">{a.owner_name || "—"}</p>
                      <p className="text-[11px] text-slate-500 truncate">{a.owner_email || "—"}</p>
                    </td>
                    <td className="px-4 py-3.5">
                      <p className="text-cyan-300 text-xs font-mono2 truncate" data-testid={`access-username-${a.id}`}>{a.username}</p>
                      <p className="text-[11px] text-slate-500 truncate">{a.name || ""}</p>
                    </td>
                    <td className="px-4 py-3.5"><span className="text-[11px] text-slate-400 font-mono2">{a.bot_username ? `@${a.bot_username}` : "—"}</span></td>
                    <td className="px-4 py-3.5">
                      <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-1 rounded-md border ${cls}`} data-testid={`access-status-${a.id}`}>{label}</span>
                    </td>
                    <td className="px-4 py-3.5"><span className="text-xs text-slate-400 font-mono2">{fmtDate(a.created_at)}</span></td>
                    <td className="px-4 py-3.5" data-testid={`access-expiry-${a.id}`}>
                      {a.expires_at ? (
                        <div>
                          <p className="text-xs text-slate-300 font-mono2">{fmtDate(a.expires_at)}</p>
                          <p className={`text-[10px] font-mono2 uppercase tracking-widest mt-0.5 ${a.expired ? "text-rose-400" : (daysLeft(a.expires_at) ?? 0) <= 5 ? "text-amber-300" : "text-emerald-300"}`}>{daysLeftText(a.expires_at)}</p>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-600">Sem validade</span>
                      )}
                    </td>
                    <td className="px-4 py-3.5"><span className="text-xs text-slate-400 font-mono2">{a.active_streams || 0}/{a.max_concurrent || "∞"}</span></td>
                    <td className="px-6 py-3.5">
                      <div className="flex items-center justify-end gap-1.5">
                        <button data-testid={`access-toggle-${a.id}`} onClick={() => toggle(a)} disabled={busy === `tg-${a.id}`} title={paused ? "Reativar" : "Pausar (corta o streaming na hora)"} className={`p-2 rounded-lg btn-ghost-dark ${paused ? "text-emerald-400" : "text-amber-400"}`}>
                          {busy === `tg-${a.id}` ? <Spinner /> : paused ? <CirclePlay size={14} /> : <Ban size={14} />}
                        </button>
                        <button data-testid={`access-renew-${a.id}`} onClick={() => { setRenewAcc(a); setRenewDays(30); }} title="Renovar validade" className="p-2 rounded-lg btn-ghost-dark text-cyan-300 hover:text-white"><CalendarClock size={14} /></button>
                        <button data-testid={`access-delete-${a.id}`} onClick={() => setConfirmDel(a)} title="Excluir acesso" className="p-2 rounded-lg btn-ghost-dark text-rose-400"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={!!renewAcc} onClose={() => setRenewAcc(null)} title={`Renovar acesso ${renewAcc?.username || ""}`} testid="admin-access-renew-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-400">Os dias somam à validade atual (se já venceu, contam a partir de hoje). O acesso volta a funcionar de imediato.</p>
          <div>
            <label className="text-xs font-mono2 uppercase tracking-widest text-slate-400 block mb-2">Dias a adicionar</label>
            <input data-testid="admin-access-renew-days" type="number" min="1" step="1" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={renewDays} onChange={(e) => setRenewDays(e.target.value)} />
          </div>
          <div className="flex gap-3 justify-end">
            <button data-testid="admin-access-renew-cancel" onClick={() => setRenewAcc(null)} className="btn-ghost-dark px-5 py-2.5 rounded-xl text-xs text-slate-300">Cancelar</button>
            <button data-testid="admin-access-renew-confirm" onClick={renew} disabled={busy === "renew"} className="btn-neon px-5 py-2.5 rounded-xl text-xs font-semibold inline-flex items-center gap-2">
              {busy === "renew" ? <Spinner /> : <CalendarClock size={13} />} Renovar
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!confirmDel} onClose={() => setConfirmDel(null)} title="Excluir acesso" testid="admin-access-delete-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-400">O acesso <span className="text-cyan-300 font-mono2">{confirmDel?.username}</span> do cliente <b className="text-white">{confirmDel?.owner_name || confirmDel?.owner_email}</b> deixa de funcionar de imediato. Esta ação não pode ser desfeita.</p>
          <div className="flex gap-3 justify-end">
            <button data-testid="admin-access-delete-cancel" onClick={() => setConfirmDel(null)} className="btn-ghost-dark px-5 py-2.5 rounded-xl text-xs text-slate-300">Cancelar</button>
            <button data-testid="admin-access-delete-confirm" onClick={remove} disabled={busy === "del"} className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-rose-500/90 hover:bg-rose-500 text-white inline-flex items-center gap-2">
              {busy === "del" ? <Spinner /> : <Trash2 size={13} />} Excluir acesso
            </button>
          </div>
        </div>
      </Modal>
    </GlassCard>
  );
}

function QueueLimitsCard() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/settings/queue").then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/queue", { max_parallel: Number(cfg.max_parallel), max_per_kind: Number(cfg.max_per_kind), job_concurrency: Number(cfg.job_concurrency), stall_minutes: Number(cfg.stall_minutes) });
      toast.success("Limites da fila guardados — aplicados em segundos às próximas importações");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const FIELDS = [
    ["max_per_kind", "Por tipo (filmes OU séries)", "Quantas importações do MESMO tipo correm ao mesmo tempo"],
    ["max_parallel", "Total em simultâneo", "Teto absoluto de importações a correr (todos os tipos)"],
    ["job_concurrency", "Itens por importação", "Downloads/uploads paralelos DENTRO de cada importação"],
    ["stall_minutes", "Vigia anti-travamento (min)", "Sem progresso há X min → o processo volta sozinho à fila"],
  ];
  return (
    <GlassCard className="p-6 space-y-4" data-testid="queue-limits-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0"><Gauge size={18} className="text-cyan-400" /></div>
        <div>
          <h3 className="font-display text-white font-semibold text-sm">Fila de importações — limites</h3>
          <p className="text-[11px] text-slate-500">Controla o paralelismo sem redeploy. Demasiado alto pode saturar o servidor — sobe aos poucos.</p>
        </div>
      </div>
      {cfg === null ? <div className="flex justify-center py-6"><Spinner /></div> : (
        <>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map(([k, label, hint]) => (
              <div key={k} className="rounded-xl border border-slate-800/70 p-3.5">
                <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
                <input data-testid={`queue-limit-${k}`} type="number" min="1" max={k === "stall_minutes" ? 60 : 12} value={cfg[k]}
                  onChange={(e) => setCfg({ ...cfg, [k]: e.target.value })} className="input-dark w-full rounded-lg px-3 py-2 text-sm" />
                <p className="text-[10px] text-slate-600 mt-1.5">{hint}</p>
              </div>
            ))}
          </div>
          <button data-testid="queue-limits-save" onClick={save} disabled={saving} className="btn-neon px-5 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
            {saving ? <Spinner /> : "Guardar limites"}
          </button>
        </>
      )}
    </GlassCard>
  );
}

function SecurityCard() {
  const [blocks, setBlocks] = useState(null);
  const [seen, setSeen] = useState(null);
  const [form, setForm] = useState({ kind: "ip", value: "", note: "" });
  const [busy, setBusy] = useState("");
  const [accFilter, setAccFilter] = useState("");
  const load = () => {
    api.get("/admin/security/blocks").then((r) => setBlocks(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/security/seen-ips").then((r) => setSeen(r.data)).catch(() => setSeen([]));
  };
  useEffect(load, []);
  const add = async () => {
    setBusy("add");
    try {
      await api.post("/admin/security/blocks", form);
      toast.success(`${form.kind.toUpperCase()} ${form.value} bloqueado — deixa de conseguir assistir imediatamente`);
      setForm({ kind: "ip", value: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const toggle = async (b) => {
    setBusy(`tg-${b.id}`);
    try {
      const { data } = await api.post(`/admin/security/blocks/${b.id}/toggle`);
      toast.success(data.active ? "Regra reativada" : "Regra desativada");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const addWhitelist = async () => {
    setBusy("wl");
    try {
      await api.post("/admin/security/blocks", { kind: form.kind === "cidr" ? "cidr" : "ip", value: form.value, note: form.note, mode: "whitelist" });
      toast.success(`${form.value} na whitelist — nunca será bloqueado (e desativei bloqueios existentes dele)`);
      setForm({ kind: "ip", value: "", note: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const whitelistSeen = async (ip) => {
    setBusy(`wl-${ip}`);
    try {
      await api.post("/admin/security/blocks", { kind: "ip", value: ip, note: "whitelist (IPs vistos)", mode: "whitelist" });
      toast.success(`${ip} na whitelist — nunca bloqueado`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const del = async (b) => {
    setBusy(`del-${b.id}`);
    try {
      await api.delete(`/admin/security/blocks/${b.id}`);
      toast.success("Regra removida");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const lookup = async (ip) => {
    setBusy(`lk-${ip}`);
    try {
      const { data } = await api.post("/admin/security/lookup", { ip });
      toast.success(`${ip} → AS${data.asn || "?"} · ${data.org || "ISP desconhecido"}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(""); }
  };
  const KIND_LABEL = { ip: "IP", cidr: "Rede (CIDR)", asn: "ASN", isp: "ISP" };
  const allUsernames = [...new Set((seen || []).flatMap((r) => r.usernames || []))].sort();
  const seenFiltered = accFilter ? (seen || []).filter((r) => (r.usernames || []).includes(accFilter)) : (seen || []);
  return (
    <GlassCard className="p-6 space-y-5" data-testid="security-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-rose-400/10 border border-rose-400/25 flex items-center justify-center shrink-0"><ShieldBan size={18} className="text-rose-400" /></div>
        <div>
          <h3 className="font-display text-white font-semibold text-sm">Bloqueio de IPs / ASN / ISP</h3>
          <p className="text-[11px] text-slate-500">IP, rede (CIDR), ASN ou ISP bloqueado deixa de assistir em TODA a plataforma, na hora — playlist, player e streams respondem 403.</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-800/70 p-4 space-y-3">
        <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Adicionar bloqueio</p>
        <div className="flex flex-wrap gap-2">
          <select data-testid="block-kind" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="input-dark rounded-lg px-3 py-2 text-xs">
            <option value="ip">IP exato</option>
            <option value="cidr">Rede CIDR (ex.: 203.0.113.0/24)</option>
            <option value="asn">ASN (ex.: AS15169)</option>
            <option value="isp">ISP pelo nome (ex.: MEO, NOS)</option>
          </select>
          <input data-testid="block-value" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })}
            placeholder={form.kind === "ip" ? "203.0.113.45" : form.kind === "cidr" ? "203.0.113.0/24" : form.kind === "asn" ? "AS15169" : "Nome do ISP"}
            className="input-dark rounded-lg px-3 py-2 text-xs flex-1 min-w-44" />
          <input data-testid="block-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Nota (opcional)" className="input-dark rounded-lg px-3 py-2 text-xs flex-1 min-w-32" />
          <button data-testid="block-add-btn" onClick={add} disabled={busy === "add" || !form.value.trim()} className="px-4 py-2 rounded-lg text-xs font-semibold bg-rose-500/90 hover:bg-rose-500 text-white inline-flex items-center gap-2 disabled:opacity-40">
            {busy === "add" ? <Spinner /> : <Ban size={13} />} Bloquear
          </button>
          {(form.kind === "ip" || form.kind === "cidr") && (
            <button data-testid="whitelist-add-btn" onClick={addWhitelist} disabled={busy === "wl" || !form.value.trim()} className="px-4 py-2 rounded-lg text-xs font-semibold bg-emerald-500/90 hover:bg-emerald-500 text-white inline-flex items-center gap-2 disabled:opacity-40">
              {busy === "wl" ? <Spinner /> : <ShieldCheck size={13} />} Permitir (whitelist)
            </button>
          )}
        </div>
        <p className="text-[10px] text-slate-600">Whitelist: esse IP/rede nunca é bloqueado (ignora bloqueios manuais e automáticos). Só IP exato ou rede CIDR.</p>
      </div>

      <div className="space-y-2">
        <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Regras de bloqueio e whitelist</p>
        {blocks === null ? <div className="flex justify-center py-6"><Spinner /></div>
          : blocks.length === 0 ? <p className="text-xs text-slate-600" data-testid="blocks-empty">Sem regras — a plataforma está aberta a todos os IPs.</p>
          : blocks.map((b) => {
            const isWl = b.mode === "whitelist";
            return (
            <div key={b.id} className={`flex flex-wrap items-center gap-2.5 px-4 py-2.5 rounded-xl border ${isWl ? "border-emerald-500/30 bg-emerald-500/5" : "border-slate-800/70"}`} data-testid={`block-row-${b.id}`}>
              <span className={`text-[9px] font-mono2 uppercase tracking-widest px-1.5 py-0.5 rounded border ${isWl ? "text-emerald-300 border-emerald-400/40 bg-emerald-400/10" : b.active ? "text-rose-300 border-rose-400/40 bg-rose-400/10" : "text-slate-500 border-slate-700"}`}>
                {isWl ? "Whitelist" : b.active ? "Bloqueado" : "Off"}
              </span>
              <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 border border-slate-700 rounded px-1.5 py-0.5">{KIND_LABEL[b.kind] || b.kind}</span>
              <span className="text-xs text-white font-mono2 font-semibold" data-testid={`block-value-${b.id}`}>{b.kind === "asn" ? `AS${b.value}` : b.value}</span>
              {b.note ? <span className="text-[10px] text-slate-500 truncate max-w-44" title={b.note}>{b.note}</span> : null}
              {!isWl && <span className="text-[10px] font-mono2 text-slate-600" data-testid={`block-hits-${b.id}`}>{b.hits || 0} bloqueio(s)</span>}
              <div className="flex-1" />
              <button data-testid={`block-toggle-${b.id}`} onClick={() => toggle(b)} disabled={busy === `tg-${b.id}`}
                className={`px-3 py-1.5 rounded-lg text-[10px] font-semibold border transition-colors ${b.active ? "border-slate-700 text-slate-300 hover:bg-slate-800" : isWl ? "border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10" : "border-rose-400/40 text-rose-300 hover:bg-rose-400/10"}`}>
                {busy === `tg-${b.id}` ? <Spinner /> : b.active ? "Desativar" : "Reativar"}
              </button>
              <button data-testid={`block-delete-${b.id}`} onClick={() => del(b)} disabled={busy === `del-${b.id}`} className="p-1.5 rounded-lg btn-ghost-dark text-slate-500 hover:text-rose-300"><Trash2 size={12} /></button>
            </div>
            );
          })}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">IPs vistos a assistir (recentes)</p>
          {allUsernames.length > 0 && (
            <select data-testid="seen-filter-access" value={accFilter} onChange={(e) => setAccFilter(e.target.value)} className="input-dark rounded-lg px-2.5 py-1 text-[11px]">
              <option value="">Todos os acessos</option>
              {allUsernames.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          )}
          {accFilter && <span className="text-[10px] font-mono2 text-cyan-300" data-testid="seen-filter-count">{seenFiltered.length} IP(s) neste acesso</span>}
        </div>
        {seen === null ? <div className="flex justify-center py-6"><Spinner /></div>
          : seen.length === 0 ? <p className="text-xs text-slate-600" data-testid="seen-empty">Ainda não há IPs registados no streaming.</p>
          : (
            <div className="max-h-72 overflow-y-auto space-y-1.5 pr-1">
              {seenFiltered.length === 0 && <p className="text-xs text-slate-600 py-3 text-center" data-testid="seen-filter-empty">Nenhum IP registado para este acesso.</p>}
              {seenFiltered.map((r) => (
                <div key={r.ip} className="flex flex-wrap items-center gap-2.5 px-4 py-2 rounded-lg border border-slate-800/60" data-testid={`seen-ip-${r.ip}`}>
                  <span className="text-xs text-white font-mono2 font-semibold">{r.ip}</span>
                  <span className="text-[10px] text-slate-500">{(r.usernames || []).join(", ") || "—"}</span>
                  <span className="text-[10px] font-mono2 text-slate-600">{r.asn ? `AS${r.asn} · ${r.org || "?"}` : "ASN não resolvido"}</span>
                  <div className="flex-1" />
                  {!r.asn && (
                    <button data-testid={`seen-lookup-${r.ip}`} onClick={() => lookup(r.ip)} disabled={busy === `lk-${r.ip}`} className="px-2.5 py-1 rounded-lg text-[10px] border border-slate-700 text-slate-400 hover:text-cyan-300 hover:border-cyan-400/40 transition-colors inline-flex items-center gap-1">
                      {busy === `lk-${r.ip}` ? <Spinner /> : <Network size={11} />} ASN/ISP
                    </button>
                  )}
                  {r.whitelisted && <span className="text-[9px] font-mono2 uppercase tracking-widest text-emerald-300 border border-emerald-400/40 bg-emerald-400/10 rounded px-1.5 py-0.5">Whitelist</span>}
                  {r.blocked ? <span className="text-[9px] font-mono2 uppercase tracking-widest text-rose-300 border border-rose-400/40 bg-rose-400/10 rounded px-1.5 py-0.5">Bloqueado</span> : (
                    <>
                    <button data-testid={`seen-block-${r.ip}`} onClick={() => { setForm({ kind: "ip", value: r.ip, note: "bloqueado a partir dos IPs vistos" }); }} className="px-2.5 py-1 rounded-lg text-[10px] border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors inline-flex items-center gap-1">
                      <Ban size={11} /> Bloquear
                    </button>
                    {!r.whitelisted && (
                    <button data-testid={`seen-whitelist-${r.ip}`} onClick={() => whitelistSeen(r.ip)} disabled={busy === `wl-${r.ip}`} className="px-2.5 py-1 rounded-lg text-[10px] border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors inline-flex items-center gap-1">
                      {busy === `wl-${r.ip}` ? <Spinner /> : <ShieldCheck size={11} />} Permitir
                    </button>
                    )}
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
      </div>
    </GlassCard>
  );
}

function VodProtectionCard() {
  const [cfg, setCfg] = useState(null);
  const [attempts, setAttempts] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => {
    api.get("/admin/settings/vod-protection").then((r) => setCfg(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/security/download-attempts").then((r) => setAttempts(r.data)).catch(() => setAttempts([]));
  };
  useEffect(load, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings/vod-protection", {
        block_download_param: !!cfg.block_download_param,
        block_downloader_ua: !!cfg.block_downloader_ua,
        custom_uas: (cfg.custom_uas_text ?? (cfg.custom_uas || []).join("\n")).split("\n").map((s) => s.trim()).filter(Boolean),
        auto_block_enabled: !!cfg.auto_block_enabled,
        auto_block_max: Number(cfg.auto_block_max) || 25,
        auto_block_window_min: Number(cfg.auto_block_window_min) || 10,
      });
      toast.success("Proteção de VOD guardada — ativa em segundos");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };
  const blockIp = async (ip) => {
    try {
      await api.post("/admin/security/blocks", { kind: "ip", value: ip, note: "manual (tentativa de download)" });
      toast.success(`IP ${ip} bloqueado`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const whitelistIp = async (ip) => {
    try {
      await api.post("/admin/security/blocks", { kind: "ip", value: ip, note: "whitelist (tentativa de download)", mode: "whitelist" });
      toast.success(`IP ${ip} na whitelist — nunca bloqueado`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  if (cfg === null) return <GlassCard className="p-6" data-testid="vodprot-card"><div className="flex justify-center py-6"><Spinner /></div></GlassCard>;
  const Toggle = ({ k, title, hint }) => (
    <button type="button" data-testid={`vodprot-${k}`} aria-pressed={!!cfg[k]} onClick={() => setCfg({ ...cfg, [k]: !cfg[k] })}
      className={`w-full text-left rounded-xl border p-3.5 transition-colors flex items-start gap-3 ${cfg[k] ? "border-emerald-400/40 bg-emerald-400/5" : "border-slate-800/70 hover:border-slate-700"}`}>
      <span className={`relative w-9 h-5 rounded-full shrink-0 transition-colors mt-0.5 ${cfg[k] ? "bg-emerald-400/80" : "bg-slate-700"}`}>
        <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-[left] ${cfg[k] ? "left-[18px]" : "left-0.5"}`} />
      </span>
      <span>
        <span className="block text-xs font-semibold text-white">{title}</span>
        <span className="block text-[10px] text-slate-500 mt-0.5">{hint}</span>
      </span>
    </button>
  );
  const KIND_LABELS = { download_param: "Download forçado", downloader_ua: "Gestor de download", auto_block: "Bloqueio automático" };
  return (
    <GlassCard className="p-6 space-y-5" data-testid="vodprot-card">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-400/10 border border-amber-400/25 flex items-center justify-center shrink-0"><ShieldCheck size={18} className="text-amber-400" /></div>
        <div>
          <h3 className="font-display text-white font-semibold text-sm">Proteção anti-download de VOD</h3>
          <p className="text-[11px] text-slate-500">Nota honesta: streaming 100% à prova de captura não existe (se toca, pode ser gravado). Isto bloqueia as vias fáceis e deteta/bloqueia rajadas de download em massa.</p>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <Toggle k="block_download_param" title="Bloquear download direto (?download=1)" hint="Links que forçam 'guardar como' respondem 403. O streaming normal nos players continua a funcionar." />
        <Toggle k="block_downloader_ua" title="Bloquear gestores de download" hint="IDM, wget, curl, aria2, ffmpeg, yt-dlp, JDownloader… são rejeitados. Players normais (VLC, TiviMate, Smarters, Kodi) passam." />
      </div>
      <div>
        <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1.5">User-Agents extra a bloquear (um por linha)</label>
        <textarea data-testid="vodprot-custom-uas" rows={2} value={cfg.custom_uas_text ?? (cfg.custom_uas || []).join("\n")}
          onChange={(e) => setCfg({ ...cfg, custom_uas_text: e.target.value })}
          placeholder="ex.: MeuDownloader" className="input-dark w-full rounded-lg px-3 py-2 text-xs font-mono2" />
      </div>
      <div className="rounded-xl border border-slate-800/70 p-3.5 space-y-3">
        <Toggle k="auto_block_enabled" title="Bloqueio automático por rajada de streams" hint="Um IP que inicie demasiados streams num curto período está provavelmente a descarregar a biblioteca — entra sozinho na lista de bloqueios (aba Segurança)." />
        {cfg.auto_block_enabled && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1.5">Máx. streams…</label>
              <input data-testid="vodprot-max" type="number" min="3" max="500" value={cfg.auto_block_max} onChange={(e) => setCfg({ ...cfg, auto_block_max: e.target.value })} className="input-dark w-full rounded-lg px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 block mb-1.5">…em X minutos</label>
              <input data-testid="vodprot-window" type="number" min="1" max="120" value={cfg.auto_block_window_min} onChange={(e) => setCfg({ ...cfg, auto_block_window_min: e.target.value })} className="input-dark w-full rounded-lg px-3 py-2 text-sm" />
            </div>
          </div>
        )}
      </div>
      <button data-testid="vodprot-save" onClick={save} disabled={saving} className="btn-neon px-5 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
        {saving ? <Spinner /> : "Guardar proteção"}
      </button>

      <div className="space-y-2 pt-2 border-t border-slate-800/60">
        <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Tentativas de download bloqueadas</p>
        {attempts === null ? <div className="flex justify-center py-4"><Spinner /></div>
          : attempts.length === 0 ? <p className="text-xs text-slate-600" data-testid="vodprot-no-attempts">Nenhuma tentativa registada — tudo limpo.</p>
          : (
            <div className="max-h-64 overflow-y-auto space-y-1.5 pr-1">
              {attempts.map((a, i) => (
                <div key={i} className="flex flex-wrap items-center gap-2 px-3.5 py-2 rounded-lg border border-slate-800/60" data-testid={`vodprot-attempt-${i}`}>
                  <span className="text-[9px] font-mono2 uppercase tracking-widest px-1.5 py-0.5 rounded border text-amber-300 border-amber-400/40 bg-amber-400/10">{KIND_LABELS[a.kind] || a.kind}</span>
                  <span className="text-xs text-white font-mono2 font-semibold">{a.ip || "?"}</span>
                  <span className="text-[10px] text-slate-500">{a.username || "—"}</span>
                  <span className="text-[10px] font-mono2 text-slate-600 truncate max-w-40" title={a.ua}>{a.ua || "sem UA"}</span>
                  <span className="text-[10px] font-mono2 text-slate-600">{a.at ? new Date(a.at).toLocaleString("pt-PT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                  <div className="flex-1" />
                  {a.ip && (a.whitelisted ? (
                    <span className="text-[9px] font-mono2 uppercase tracking-widest text-emerald-300 border border-emerald-400/40 bg-emerald-400/10 rounded px-1.5 py-0.5">Whitelist</span>
                  ) : a.already_blocked ? (
                    <span className="text-[9px] font-mono2 uppercase tracking-widest text-rose-300 border border-rose-400/40 bg-rose-400/10 rounded px-1.5 py-0.5">Já bloqueado</span>
                  ) : (
                    <>
                    <button data-testid={`vodprot-block-${i}`} onClick={() => blockIp(a.ip)} className="px-2.5 py-1 rounded-lg text-[10px] border border-rose-400/40 text-rose-300 hover:bg-rose-400/10 transition-colors inline-flex items-center gap-1">
                      <Ban size={11} /> Bloquear IP
                    </button>
                    <button data-testid={`vodprot-whitelist-${i}`} onClick={() => whitelistIp(a.ip)} className="px-2.5 py-1 rounded-lg text-[10px] border border-emerald-400/40 text-emerald-300 hover:bg-emerald-400/10 transition-colors inline-flex items-center gap-1">
                      <ShieldCheck size={11} /> Permitir
                    </button>
                    </>
                  ))}
                </div>
              ))}
            </div>
          )}
      </div>
    </GlassCard>
  );
}

export default function Admin() {
  const { user: me } = useAuth();
  const [tab, setTab] = useState("geral");
  const [users, setUsers] = useState(null);
  const [userSearch, setUserSearch] = useState("");
  const [userPage, setUserPage] = useState(0);
  const [sortVenc, setSortVenc] = useState(false);
  const [invoices, setInvoices] = useState(null);
  const [userModal, setUserModal] = useState(null);
  const [invoiceModal, setInvoiceModal] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [planModal, setPlanModal] = useState(null);
  const [plans, setPlans] = useState(null);
  const [busy, setBusy] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(null);

  const load = () => {
    api.get("/admin/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/admin/invoices").then((r) => setInvoices(r.data)).catch(() => {});
    api.get("/saas/plans").then((r) => setPlans(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const toggleSuspend = async (u) => {
    const status = u.status === "suspended" ? "active" : "suspended";
    setBusy(`suspend-${u.id}`);
    try {
      await api.put(`/admin/users/${u.id}`, { status });
      toast.success(status === "suspended" ? `${u.name} suspenso — acesso bloqueado na hora` : `${u.name} reativado`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const deleteUser = async () => {
    setBusy("delete");
    try {
      await api.delete(`/admin/users/${confirmDelete.id}`);
      toast.success(`${confirmDelete.name} removido da plataforma`);
      setConfirmDelete(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const impersonate = async (u) => {
    setBusy(`imp-${u.id}`);
    try {
      await api.post(`/admin/users/${u.id}/impersonate`);
      toast.success(`A entrar como ${u.name}...`);
      setTimeout(() => window.location.assign("/app/dashboard"), 700);
    } catch (e) { toast.error(formatApiError(e)); setBusy(null); }
  };

  const purgeContent = async () => {
    setBusy("purge");
    try {
      const { data } = await api.post(`/admin/users/${confirmPurge.id}/purge-content`);
      toast.success(`Conteúdo de ${confirmPurge.name} apagado — ${data.files_removed} ficheiro(s) removidos do storage`);
      setConfirmPurge(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const markPaid = async (inv) => {
    setBusy(`pay-${inv.id}`);
    try {
      await api.post(`/admin/invoices/${inv.id}/pay`);
      toast.success(`Fatura baixada — ${fmtGB(inv.credits_gb)} creditados para ${inv.user_name || inv.user_email}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const cancelInvoice = async () => {
    setBusy("cancel");
    try {
      await api.post(`/admin/invoices/${confirmCancel.id}/cancel`);
      toast.success("Fatura cancelada");
      setConfirmCancel(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const deletePlan = async (p) => {
    setBusy(`plan-${p.id}`);
    try {
      await api.delete(`/saas/plans/${p.id}`);
      toast.success(`Plano "${p.name}" removido`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(null); }
  };

  const uq = userSearch.trim().toLowerCase();
  const filteredUsers = (users || []).filter((u) => !uq || (u.name || "").toLowerCase().includes(uq) || (u.email || "").toLowerCase().includes(uq) || (u.plan_name || "").toLowerCase().includes(uq));
  const totalPages = Math.max(1, Math.ceil(filteredUsers.length / 10));
  const safePage = Math.min(userPage, totalPages - 1);
  const pagedUsers = (sortVenc ? [...filteredUsers].sort((a, b) => (a.plan_expires_at || "9999").localeCompare(b.plan_expires_at || "9999")) : filteredUsers).slice(safePage * 10, safePage * 10 + 10);

  const TABS = [
    ["geral", "Visão Geral", LayoutDashboard],
    ["usuarios", "Usuários", ShieldCheck],
    ["acessos", "Acessos", KeyRound],
    ["financeiro", "Financeiro", FileText],
    ["suporte", "Suporte", LifeBuoy],
    ["sistema", "Sistema", Wrench],
    ["seguranca", "Segurança", ShieldBan],
    ["protecao", "Proteção VOD", ShieldCheck],
  ];

  return (
    <div className="space-y-6" data-testid="admin-page">
      <PageHeader eyebrow="Administração" title="Painel do Administrador" subtitle="Clientes, cobranças, suporte e saúde da plataforma — escolha uma seção abaixo." />

      <div className="flex gap-1.5 p-1.5 rounded-2xl border border-slate-800/60 bg-slate-950/50 backdrop-blur-xl overflow-x-auto" data-testid="admin-tabs">
        {TABS.map(([id, label, Icon]) => (
          <button key={id} data-testid={`admin-tab-${id}`} onClick={() => setTab(id)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-colors ${tab === id ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-400 border border-transparent hover:text-white"}`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "geral" && (
        <>
          <ServerStatus />
          <AdminCentral />
        </>
      )}

      {tab === "usuarios" && (
      <GlassCard className="overflow-hidden anim-fade-up" data-testid="admin-users-card">
        <div className="px-6 pt-6 pb-4 flex flex-wrap items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-400/10 border border-cyan-400/25 flex items-center justify-center shrink-0">
            <ShieldCheck size={18} className="text-cyan-400" />
          </div>
          <div className="flex-1 min-w-36">
            <h3 className="font-display text-white font-semibold text-sm">Usuários <span className="text-slate-500 font-normal text-xs">({filteredUsers.length})</span></h3>
            <p className="text-[11px] text-slate-500">Administradores têm acesso total — só não podem mexer na conta do dono</p>
          </div>
          <button data-testid="new-user-btn" onClick={() => setUserModal({ mode: "create" })} className="btn-neon px-4 py-2.5 rounded-xl text-xs inline-flex items-center gap-2">
            <UserPlus size={14} /> Novo usuário
          </button>
          <input data-testid="admin-users-search" className="input-dark rounded-lg px-3 py-2 text-xs w-52" placeholder="Buscar nome, email ou plano" value={userSearch} onChange={(e) => { setUserSearch(e.target.value); setUserPage(0); }} />
          <button data-testid="admin-users-sort-venc" onClick={() => { setSortVenc(!sortVenc); setUserPage(0); }} className={`px-3 py-2 rounded-lg text-[11px] font-semibold border transition-colors ${sortVenc ? "border-cyan-400/40 text-cyan-300 bg-cyan-400/10" : "border-slate-700 text-slate-400 hover:text-white"}`}>
            Vencimento ↑
          </button>
          <div className="flex items-center gap-1.5" data-testid="admin-users-pagination">
            <button data-testid="admin-users-prev" disabled={safePage === 0} onClick={() => setUserPage(safePage - 1)} className="px-2.5 py-2 rounded-lg btn-ghost-dark text-slate-400 disabled:opacity-30 text-sm leading-none">‹</button>
            <span className="text-[10px] font-mono2 text-slate-500">{safePage + 1}/{totalPages}</span>
            <button data-testid="admin-users-next" disabled={safePage >= totalPages - 1} onClick={() => setUserPage(safePage + 1)} className="px-2.5 py-2 rounded-lg btn-ghost-dark text-slate-400 disabled:opacity-30 text-sm leading-none">›</button>
          </div>
        </div>
        {users === null ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[10px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                  <th className="px-6 py-3">Usuário</th>
                  <th className="px-4 py-3">Papel</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Plano / Vencimento</th>
                  <th className="px-4 py-3">Storage</th>
                  <th className="px-6 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {pagedUsers.map((u) => {
                  const suspended = u.status === "suspended";
                  return (
                    <tr key={u.id} className="border-b border-slate-800/40 last:border-0" data-testid={`user-row-${u.id}`}>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-lg overflow-hidden bg-slate-800 border border-slate-700 flex items-center justify-center shrink-0">
                            {u.avatar_path ? (
                              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/auth/avatar/${u.id}`} alt="" className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-[10px] font-bold text-slate-400">{(u.name || "?").slice(0, 2).toUpperCase()}</span>
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-white text-xs font-semibold flex items-center gap-1.5 truncate">
                              {u.name}
                              {u.is_owner && <span className="inline-flex items-center gap-1 text-[9px] font-mono2 uppercase tracking-widest text-amber-300 border border-amber-400/30 bg-amber-400/10 px-1.5 py-0.5 rounded-md"><Crown size={9} /> Dono</span>}
                              {u.is_test && <span data-testid={`user-test-badge-${u.id}`} className="inline-flex items-center gap-1 text-[9px] font-mono2 uppercase tracking-widest text-fuchsia-300 border border-fuchsia-400/30 bg-fuchsia-400/10 px-1.5 py-0.5 rounded-md">Teste</span>}
                            </p>
                            <p className="text-[11px] text-slate-500 truncate">{u.email}</p>
                            <p className="text-[10px] text-slate-600 truncate" data-testid={`user-since-${u.id}`}>Cliente desde {fmtDate(u.created_at)}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-1 rounded-md border ${u.role === "super_admin" ? "text-cyan-300 border-cyan-400/30 bg-cyan-400/10" : "text-slate-400 border-slate-700 bg-slate-800/40"}`} data-testid={`user-role-${u.id}`}>
                          {ROLE_LABEL[u.role] || u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3.5">
                        <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-1 rounded-md border ${suspended ? "text-rose-300 border-rose-400/30 bg-rose-400/10" : "text-emerald-300 border-emerald-400/30 bg-emerald-400/10"}`} data-testid={`user-status-${u.id}`}>
                          {suspended ? "Suspenso" : "Ativo"}
                        </span>
                      </td>
                      <td className="px-4 py-3.5" data-testid={`user-plan-expiry-${u.id}`}>
                        {u.plan_id ? (
                          <div>
                            <p className="text-xs text-slate-300 font-mono2">{u.plan_name || u.plan_id} · {fmtDate(u.plan_expires_at)}</p>
                            <p className={`text-[10px] font-mono2 uppercase tracking-widest mt-0.5 ${(daysLeft(u.plan_expires_at) ?? 0) < 0 ? "text-rose-400" : (daysLeft(u.plan_expires_at) ?? 0) <= 5 ? "text-amber-300" : "text-emerald-300"}`}>
                              {daysLeftText(u.plan_expires_at)}
                            </p>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-600">Sem plano</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5" data-testid={`user-storage-${u.id}`}>
                        <p className="text-xs text-slate-200 font-mono2 font-semibold">{fmtBytes(u.storage_used_bytes || 0)}</p>
                        {u.plan_storage_gb ? <p className="text-[10px] font-mono2 text-slate-600">de {u.plan_storage_gb} GB</p> : <p className="text-[10px] font-mono2 text-slate-600">ocupado</p>}
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="flex items-center justify-end gap-1.5">
                          <Link data-testid={`user-view-${u.id}`} to={`/app/admin/cliente/${u.id}`} title="Ver ambiente do cliente" className="p-2 rounded-lg btn-ghost-dark text-cyan-300 hover:text-white"><Eye size={14} /></Link>
                          <button data-testid={`user-edit-${u.id}`} onClick={() => setUserModal({ mode: "edit", user: u })} title="Editar" className="p-2 rounded-lg btn-ghost-dark text-slate-400 hover:text-white"><Pencil size={14} /></button>
                          <button data-testid={`user-invoice-${u.id}`} onClick={() => setInvoiceModal({ presetUserId: u.id })} title="Criar fatura" className="p-2 rounded-lg btn-ghost-dark text-slate-400 hover:text-white"><FileText size={14} /></button>
                          {!u.is_owner && (
                            <>
                              <button data-testid={`user-impersonate-${u.id}`} onClick={() => impersonate(u)} disabled={busy === `imp-${u.id}`} title="Entrar como este utilizador (impersonar)" className="p-2 rounded-lg btn-ghost-dark text-violet-300 hover:text-white">
                                {busy === `imp-${u.id}` ? <Spinner /> : <UserCheck size={14} />}
                              </button>
                              <button data-testid={`user-purge-${u.id}`} onClick={() => setConfirmPurge(u)} title="Apagar todo o conteúdo do storage deste cliente" className="p-2 rounded-lg btn-ghost-dark text-orange-400"><Eraser size={14} /></button>
                              <button data-testid={`user-suspend-${u.id}`} onClick={() => toggleSuspend(u)} disabled={busy === `suspend-${u.id}`} title={suspended ? "Reativar" : "Suspender"} className={`p-2 rounded-lg btn-ghost-dark ${suspended ? "text-emerald-400" : "text-amber-400"}`}>
                                {busy === `suspend-${u.id}` ? <Spinner /> : suspended ? <CirclePlay size={14} /> : <Ban size={14} />}
                              </button>
                              <button data-testid={`user-delete-${u.id}`} onClick={() => setConfirmDelete(u)} title="Excluir" className="p-2 rounded-lg btn-ghost-dark text-rose-400"><Trash2 size={14} /></button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
      )}

      {tab === "acessos" && <AccessesAdmin />}

      <Modal open={!!confirmPurge} onClose={() => setConfirmPurge(null)} title="Apagar conteúdo do cliente" testid="purge-content-modal">
        <div className="space-y-4">
          <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3">
            <p className="text-sm text-rose-200 leading-relaxed">
              Todos os ficheiros de <b className="text-white">{confirmPurge?.name}</b> serão apagados do armazenamento em nuvem e a cota de storage é libertada. Os links M3U dele morrem na hora. <b>Irreversível.</b>
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <button data-testid="purge-content-cancel" onClick={() => setConfirmPurge(null)} className="btn-ghost-dark px-5 py-2.5 rounded-xl text-xs text-slate-300">Cancelar</button>
            <button data-testid="purge-content-confirm" onClick={purgeContent} disabled={busy === "purge"} className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-rose-500/90 hover:bg-rose-500 text-white inline-flex items-center gap-2">
              {busy === "purge" ? <Spinner /> : <Eraser size={13} />} Apagar conteúdo
            </button>
          </div>
        </div>
      </Modal>

      {tab === "suporte" && <TicketsAdmin />}

      {tab === "financeiro" && (
        <>
      <GlassCard className="overflow-hidden anim-fade-up" data-testid="admin-plans-card">
        <div className="px-6 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-violet-400/10 border border-violet-400/25 flex items-center justify-center">
              <HardDrive size={18} className="text-violet-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Planos de armazenamento</h3>
              <p className="text-[11px] text-slate-500">Tabela mensal exibida aos clientes — acima de 10 TB o preço calcula sozinho (custo + 30%)</p>
            </div>
          </div>
          <button data-testid="new-plan-btn" onClick={() => setPlanModal({})} className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs font-semibold text-white inline-flex items-center gap-2">
            <UserPlus size={14} /> Novo plano
          </button>
        </div>
        {plans === null ? (
          <div className="flex justify-center py-8"><Spinner /></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="text-left text-[10px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                  <th className="px-6 py-3">Plano</th>
                  <th className="px-4 py-3">Storage</th>
                  <th className="px-4 py-3">Preço</th>
                  <th className="px-6 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((p) => (
                  <tr key={p.id} className="border-b border-slate-800/40 last:border-0" data-testid={`plan-row-${p.id}`}>
                    <td className="px-6 py-3.5">
                      <span className="text-white text-xs font-semibold">{p.name}</span>
                      {p.badge && <span className="ml-2 text-[9px] font-mono2 uppercase tracking-widest text-slate-500 border border-slate-700 rounded-md px-1.5 py-0.5">{p.badge}</span>}
                    </td>
                    <td className="px-4 py-3.5 text-xs text-slate-300 font-mono2">{fmtStorage(p.storage_gb)}</td>
                    <td className="px-4 py-3.5 text-xs text-slate-300 font-mono2">{fmtEUR(p.price_eur ?? p.price_brl)}/mês</td>
                    <td className="px-6 py-3.5 text-right">
                      {p.custom ? (
                        <button data-testid={`plan-delete-${p.id}`} onClick={() => deletePlan(p)} disabled={busy === `plan-${p.id}`} title="Excluir" className="p-2 rounded-lg btn-ghost-dark text-rose-400">
                          {busy === `plan-${p.id}` ? <Spinner /> : <Trash2 size={14} />}
                        </button>
                      ) : (
                        <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-600">padrão</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
      {planModal && <PlanModal onClose={() => setPlanModal(null)} onSaved={() => { setPlanModal(null); load(); }} />}

      <GlassCard className="overflow-hidden anim-fade-up anim-delay-1" data-testid="admin-invoices-card">
        <div className="px-6 pt-6 pb-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-purple-400/10 border border-purple-400/25 flex items-center justify-center">
              <FileText size={18} className="text-purple-400" />
            </div>
            <div>
              <h3 className="font-display text-white font-semibold text-sm">Faturas</h3>
              <p className="text-[11px] text-slate-500">Crie, edite, mude o vencimento, dê baixa ou cancele</p>
            </div>
          </div>
          <button data-testid="new-invoice-btn" onClick={() => setInvoiceModal({})} className="btn-ghost-dark px-4 py-2.5 rounded-xl text-xs font-semibold text-white inline-flex items-center gap-2">
            <FileText size={14} /> Nova fatura
          </button>
        </div>
        {invoices === null ? (
          <div className="flex justify-center py-10"><Spinner /></div>
        ) : invoices.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-slate-500" data-testid="invoices-empty">Nenhuma fatura ainda. Clique em "Nova fatura" para cobrar um cliente.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead>
                <tr className="text-left text-[10px] font-mono2 uppercase tracking-widest text-slate-500 border-b border-slate-800/60">
                  <th className="px-6 py-3">Cliente</th>
                  <th className="px-4 py-3">Descrição</th>
                  <th className="px-4 py-3">Valor</th>
                  <th className="px-4 py-3">Créditos</th>
                  <th className="px-4 py-3">Vencimento</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-6 py-3 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((inv) => {
                  const st = INV_STATUS[inv.status] || INV_STATUS.pending;
                  return (
                    <tr key={inv.id} className="border-b border-slate-800/40 last:border-0" data-testid={`invoice-row-${inv.id}`}>
                      <td className="px-6 py-3.5">
                        <p className="text-white text-xs font-semibold truncate">{inv.user_name || "—"}</p>
                        <p className="text-[11px] text-slate-500 truncate">{inv.user_email}</p>
                      </td>
                      <td className="px-4 py-3.5 text-xs text-slate-300">{inv.title}</td>
                      <td className="px-4 py-3.5 text-xs text-white font-semibold font-mono2" data-testid={`invoice-amount-${inv.id}`}>{fmtPay(inv)}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-300 font-mono2">{fmtGB(inv.credits_gb)}</td>
                      <td className="px-4 py-3.5 text-xs text-slate-300" data-testid={`invoice-due-${inv.id}`}>{fmtDate(inv.due_date)}</td>
                      <td className="px-4 py-3.5">
                        <span className={`text-[10px] font-mono2 uppercase tracking-widest px-2 py-1 rounded-md border ${st.cls}`} data-testid={`invoice-status-${inv.id}`}>{st.label}</span>
                      </td>
                      <td className="px-6 py-3.5">
                        {inv.status === "pending" && (
                          <div className="flex items-center justify-end gap-1.5">
                            <button data-testid={`invoice-edit-${inv.id}`} onClick={() => setInvoiceModal({ edit: inv })} title="Editar / alterar vencimento" className="p-2 rounded-lg btn-ghost-dark text-slate-400 hover:text-white"><Pencil size={14} /></button>
                            <button data-testid={`invoice-pay-${inv.id}`} onClick={() => markPaid(inv)} disabled={busy === `pay-${inv.id}`} title="Marcar como paga (credita o saldo)" className="p-2 rounded-lg btn-ghost-dark text-emerald-400">
                              {busy === `pay-${inv.id}` ? <Spinner /> : <CheckCircle2 size={14} />}
                            </button>
                            <button data-testid={`invoice-cancel-${inv.id}`} onClick={() => setConfirmCancel(inv)} title="Cancelar fatura" className="p-2 rounded-lg btn-ghost-dark text-rose-400"><XCircle size={14} /></button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </GlassCard>
        </>
      )}

      {tab === "sistema" && (
        <>
          <StorageCard />
          <YtCookiesCard />
          <ReconcileCard />
        </>
      )}

      {tab === "seguranca" && (
        <>
          <QueueLimitsCard />
          <SecurityCard />
        </>
      )}

      {tab === "protecao" && <VodProtectionCard />}

      {userModal && <UserModal mode={userModal.mode} user={userModal.user} onClose={() => setUserModal(null)} onSaved={() => { setUserModal(null); load(); }} />}
      {invoiceModal && <InvoiceModal users={users || []} edit={invoiceModal.edit} presetUserId={invoiceModal.presetUserId} onClose={() => setInvoiceModal(null)} onSaved={() => { setInvoiceModal(null); load(); }} />}

      <Modal open={!!confirmDelete} onClose={() => setConfirmDelete(null)} title="Excluir usuário" testid="delete-user-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-400">
            <span className="text-white font-semibold">{confirmDelete?.name}</span> ({confirmDelete?.email}) será removido da plataforma e perde o acesso imediatamente. Essa ação não pode ser desfeita.
          </p>
          <div className="flex gap-3 justify-end">
            <button data-testid="delete-user-cancel" onClick={() => setConfirmDelete(null)} className="btn-ghost-dark px-5 py-2.5 rounded-xl text-xs text-slate-300">Cancelar</button>
            <button data-testid="delete-user-confirm" onClick={deleteUser} disabled={busy === "delete"} className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-rose-500/90 hover:bg-rose-500 text-white inline-flex items-center gap-2">
              {busy === "delete" ? <Spinner /> : <Trash2 size={13} />} Excluir
            </button>
          </div>
        </div>
      </Modal>

      <Modal open={!!confirmCancel} onClose={() => setConfirmCancel(null)} title="Cancelar fatura" testid="cancel-invoice-modal">
        <div className="space-y-4">
          <p className="text-sm text-slate-400">
            A fatura <span className="text-white font-semibold">{confirmCancel?.title}</span> de {fmtPay(confirmCancel)} para {confirmCancel?.user_email} será cancelada e o cliente não poderá mais pagá-la.
          </p>
          <div className="flex gap-3 justify-end">
            <button data-testid="cancel-invoice-back" onClick={() => setConfirmCancel(null)} className="btn-ghost-dark px-5 py-2.5 rounded-xl text-xs text-slate-300">Voltar</button>
            <button data-testid="cancel-invoice-confirm" onClick={cancelInvoice} disabled={busy === "cancel"} className="px-5 py-2.5 rounded-xl text-xs font-semibold bg-rose-500/90 hover:bg-rose-500 text-white inline-flex items-center gap-2">
              {busy === "cancel" ? <Spinner /> : <XCircle size={13} />} Cancelar fatura
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
